/**
 * Geohash Servis
 * Geohash'lerle ilgili API çağrılarını içerir.
 */
import authService from "./auth.service";
import i18n from "../i18n";
import siteConfig from "../siteConfig";
const t = (key, opts) => i18n.t(key, opts);

// API yapılandırması
const API_CONFIG = {
  BASE_URL: siteConfig.apiBaseUrl,
  // API endpoint'leri
  ENDPOINTS: {
    EXPLORE: "/world/explore",
    GEOHASHES: "/world/geohashes",
    DISCOVER: "/world/discover",
    VOTE: "/world/vote",
    REPORT: "/world/report", // Raporlama endpoint'i
    ROUTE: "/world/route", // Route discovery endpoint'i (query parameter: geohashes)
    // Kullanıcı API endpoint'leri
    USER_UPLOAD: "/user/upload-image/to/{geohash}",
    SCOREBOARD: "/world/scoreboard", // Skor tablosu endpoint'i
  },
};

class GeohashService {
  /**
   * Belirtilen geohash'ler için keşif verisini getirir.
   * @param {Array<string>} geohashes - Sorgulanacak geohash'ler dizisi
   * @returns {Promise<any>} - API yanıtı
   */
  /**
   * Tüm world 7'li geohash datasını tek seferde getirir (clustering / ana harita için).
   * Image'lar haritada sadece görünür olan hücreler render edilirken yüklenir.
   * @returns {Promise<{ data: Object }>} - data: { [geohash7]: { url, score, user, geohash_id, user_links, is_commercial } }
   */
  static async getAllWorldGeohashes() {
    try {
      const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.GEOHASHES}`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error(t("errors.apiError", { status: response.status }));
      }
      return await response.json();
    } catch (error) {
      console.error("World geohash verisi alınırken hata:", error);
      throw error;
    }
  }

  /**
   * @param {{ south: number, north: number, west: number, east: number }} bbox
   */
  static async getExploreData(bbox) {
    try {
      const url = new URL(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.EXPLORE}`,
      );
      url.searchParams.append("south", bbox.south);
      url.searchParams.append("north", bbox.north);
      url.searchParams.append("west", bbox.west);
      url.searchParams.append("east", bbox.east);

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(t("errors.apiError", { status: response.status }));
      }

      return await response.json();
    } catch (error) {
      console.error("Geohash keşif verisi alınırken hata oluştu:", error);
      throw error;
    }
  }

  /**
   * Belirli bir geohash ID için fotoğrafları getirir
   * @param {string} geohashId - Fotoğrafları getirmek için geohash ID
   * @returns {Promise<any>} - API yanıtı
   */
  static async getDiscoverData(geohashId) {
    if (!geohashId) {
      throw new Error(t("errors.geohashRequired"));
    }

    try {
      const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.DISCOVER}/${geohashId}`;

      // Auth token kontrolü
      const headers = {
        "Content-Type": "application/json",
        Accept: "application/json",
      };

      const response = await fetch(url, {
        method: "GET",
        headers,
        credentials: "include", // Cookie'leri dahil et
      });

      if (!response.ok) {
        // Kullanıcı kimlik doğrulaması gerekliyse
        if (response.status === 401) {
          throw new Error(t("errors.authRequired"));
        }
        throw new Error(t("errors.apiError", { status: response.status }));
      }

      return await response.json();
    } catch (error) {
      console.error("Geohash fotoğrafları alınırken hata oluştu:", error);
      throw error;
    }
  }

  /**
   * Bir fotoğrafa oy verir (beğeni/beğenmeme)
   * @param {string} imageId - Oy verilecek fotoğrafın ID'si
   * @param {boolean} isLiked - Beğenildi mi (true) yoksa beğenilmedi mi (false)
   * @param {string} username - Oy veren kullanıcının adı
   * @returns {Promise<any>} - API yanıtı
   */
  static async voteImage(imageId, isLiked, username) {
    if (!imageId || typeof isLiked !== "boolean" || !username) {
      throw new Error(t("errors.authRequired"));
    }

    // Kullanıcı giriş yapmış mı kontrol et
    if (!authService.isLoggedIn()) {
      throw new Error(t("errors.authRequired"));
    }

    try {
      const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.VOTE}`;

      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include", // Cookie'leri dahil et
        body: JSON.stringify({
          imageId: imageId,
          is_liked: isLiked,
          user: username,
        }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error(t("errors.authRequired"));
        }
        throw new Error(t("errors.apiError", { status: response.status }));
      }

      return await response.json();
    } catch (error) {
      console.error("Fotoğrafa oy verirken hata oluştu:", error);
      throw error;
    }
  }

  /**
   * Fotoğraf yükleme API çağrısı
   * @param {File} file - Yüklenecek fotoğraf dosyası
   * @param {string} geohash - Fotoğrafın ait olduğu geohash7 kodu
   * @param {{ description?: string, categories?: string[], lat?: number, lng?: number }} options - Opsiyonel: description, categories, lat, lng
   * @returns {Promise<any>} - API yanıtı
   */
  static async uploadPhoto(file, geohash, options = {}) {
    if (!file || !geohash) {
      throw new Error(t("errors.fileAndGeohashRequired"));
    }

    // Geohash uzunluğunu kontrol et (precision 7 olmalı)
    if (geohash.length !== 7) {
      throw new Error(t("errors.geohashPrecision"));
    }

    const { description, categories, lat, lng } = options;

    // FormData oluştur
    const formData = new FormData();
    formData.append("file", file);
    if (description) formData.append("description", description);
    if (Array.isArray(categories) && categories.length > 0) {
      formData.append("categories", categories.join(","));
    }
    if (typeof lat === "number" && !Number.isNaN(lat))
      formData.append("lat", String(lat));
    if (typeof lng === "number" && !Number.isNaN(lng))
      formData.append("lng", String(lng));

    try {
      // URL oluştur - yalnızca geohash değerini URL yoluna ekle
      const endpoint = API_CONFIG.ENDPOINTS.USER_UPLOAD.replace(
        "{geohash}",
        geohash,
      );

      const url = `${API_CONFIG.BASE_URL}${endpoint}`;

      // POST isteği gönder
      const response = await fetch(url, {
        method: "POST",
        headers: {
          // FormData için Content-Type header'ı otomatik olarak ayarlanır
        },
        credentials: "include", // Cookie'leri dahil et
        body: formData,
      });

      // Başarısız yanıtları kontrol et
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error(t("errors.authRequired"));
        }

        const errorData = await response.json().catch(() => ({
          message: t("errors.apiError", { status: "" }),
        }));
        throw new Error(
          errorData.message || t("errors.uploadFailed", { status: response.status }),
        );
      }

      // Başarılı yanıtı JSON olarak dönüştür
      return await response.json();
    } catch (error) {
      console.error("Fotoğraf yükleme hatası:", error);
      throw error;
    }
  }

  /**
   * Skor tablosunu getirir
   * @returns {Promise<any>} - Skor tablosu verileri
   */
  static async getScoreboard() {
    try {
      const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SCOREBOARD}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include", // Cookie'leri dahil et
      });

      if (!response.ok) {
        // Backend artık public; yine de 401/unauth gelirse scoreboard'ı boş göster.
        if (response.status === 401) return { data: [] };
        throw new Error(t("errors.apiError", { status: response.status }));
      }

      return await response.json();
    } catch (error) {
      console.error("Skor tablosu alınırken hata oluştu:", error);
      throw error;
    }
  }

  /**
   * Bir fotoğrafı uygunsuz içerik olarak bildir
   * @param {string} imageId - Bildirilecek fotoğrafın ID'si
   * @returns {Promise<any>} - API yanıtı
   */
  static async reportImage(imageId) {
    if (!imageId) {
      throw new Error(t("errors.photoIdRequired"));
    }

    // Kullanıcı giriş yapmış mı kontrol et
    if (!authService.isLoggedIn()) {
      throw new Error(t("errors.authRequired"));
    }

    try {
      const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.REPORT}`;

      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include", // Cookie'leri dahil et
        body: JSON.stringify({
          image_id: imageId,
        }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error(t("errors.authRequired"));
        }
        throw new Error(t("errors.apiError", { status: response.status }));
      }

      return await response.json();
    } catch (error) {
      console.error("Fotoğraf bildirilirken hata oluştu:", error);
      throw error;
    }
  }

  /**
   * Kullanıcı etkileşimlerini loglar (fire-and-forget, cevap beklemez)
   * @param {string} interactionType - Etkileşim tipi ("camera" veya "avatar")
   */
  static logInteraction(interactionType) {
    if (!interactionType) {
      return; // Sessizce çık
    }

    // Fire-and-forget: Cevap beklemeden devam et, frontend'i bloklamasın
    // Path'te type var, body'ye gerek yok
    fetch(`${API_CONFIG.BASE_URL}/world/interaction/${interactionType}`, {
      method: "POST",
    }).catch(() => {
      // Sessizce devam et, hata loglamaya gerek yok
    });
  }

  /**
   * Belirtilen geohash(ler) için route verisini getirir.
   * REST mimarisine uygun şekilde query parameter kullanır.
   * @param {string|Array<string>} geohashes - Route için geohash veya geohash dizisi
   * @param {Array<string>} categories - Kategori filtreleri (opsiyonel): ["nature", "architecture", "cafe"]
   * @returns {Promise<any>} - API yanıtı (explore endpoint'i ile aynı format)
   */
  static async getRoute(geohashes, categories = []) {
    if (!geohashes || (Array.isArray(geohashes) && geohashes.length === 0)) {
      throw new Error(t("errors.geohashRequired"));
    }

    try {
      // Eğer array ise virgülle ayrılmış string'e çevir, tek geohash ise direkt kullan
      const geohashParam = Array.isArray(geohashes)
        ? geohashes.join(",")
        : geohashes;

      // REST mimarisine uygun: query parameter kullan
      const url = new URL(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ROUTE}`,
      );
      url.searchParams.append("geohashes", geohashParam);

      // Kategorileri ekle (varsa)
      if (categories && Array.isArray(categories) && categories.length > 0) {
        const categoryParam = categories.join(",");
        url.searchParams.append("categories", categoryParam);
      }

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(t("errors.apiError", { status: response.status }));
      }

      return await response.json();
    } catch (error) {
      console.error("Rota verisi alınırken hata oluştu:", error);
      throw error;
    }
  }
}

export default GeohashService;
